import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, X, Sparkles, Zap, Crown, ArrowLeft, Star, Shield, Clock, Rocket, Users, HeadphonesIcon } from 'lucide-react';

export default function PricingPage({ onBackToHome, currentUser, onShowVIPDialog }) {
  const [isAnnual, setIsAnnual] = useState(false);
  
  const handleUpgradeToPro = () => {
    if (currentUser) {
      // Show VIP code dialog
      onShowVIPDialog();
    } else {
      // Need to login first
      onBackToHome();
    }
  };

  const features = {
    free: [
      { name: '10 AI-optimized prompts per month', included: true },
      { name: 'Access to all prompt templates', included: true },
      { name: 'Basic AI tool recommendations', included: true },
      { name: 'Standard formatting', included: true },
      { name: 'Email support', included: true },
      { name: 'Advanced prompting techniques', included: false },
      { name: 'Priority AI processing', included: false },
      { name: 'Custom prompt templates', included: false },
      { name: 'API access', included: false },
      { name: 'Premium support (24/7)', included: false },
    ],
    pro: [
      { name: 'Unlimited AI-optimized prompts', included: true },
      { name: 'Access to all prompt templates', included: true },
      { name: 'Premium AI tool recommendations', included: true },
      { name: 'Advanced formatting & styling', included: true },
      { name: 'Priority email & chat support', included: true },
      { name: 'Advanced prompting techniques', included: true },
      { name: 'Priority AI processing (2x faster)', included: true },
      { name: 'Custom prompt templates', included: true },
      { name: 'API access (coming soon)', included: true },
      { name: 'Premium support (24/7)', included: true },
    ]
  };

  const monthlyPrice = 999;
  const annualPrice = 11999; // ₹11,999/year (₹999.92/month equivalent)

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <nav className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onBackToHome} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-tight">PromptCraft AI</span>
                <span className="text-xs text-muted-foreground">by Growthyfai</span>
              </div>
            </div>
            <Button onClick={onBackToHome} variant="outline" size="sm">
              Home
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        
        {/* Header Section */}
        <div className="text-center mb-12 animate-fade-in">
          <Badge className="mb-4 px-4 py-2 text-sm">
            <Sparkles className="w-4 h-4 mr-2" />
            Simple, Transparent Pricing
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            Choose Your <span className="gradient-text">Perfect Plan</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Get access to AI-powered prompt engineering tools that transform your simple ideas into professional, detailed prompts
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4 mb-4">
            <span className={`text-sm font-medium ${!isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Monthly
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative w-14 h-7 rounded-full transition-colors ${
                isAnnual ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                  isAnnual ? 'translate-x-7' : 'translate-x-0'
                }`}
              />
            </button>
            <span className={`text-sm font-medium ${isAnnual ? 'text-foreground' : 'text-muted-foreground'}`}>
              Annual
            </span>
            {isAnnual && (
              <Badge variant="secondary" className="animate-pulse">
                Pay Yearly
              </Badge>
            )}
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto mb-20">
          
          {/* Free Plan */}
          <Card className="relative overflow-hidden border-2 hover:shadow-xl transition-all duration-300 animate-slide-up">
            <CardHeader className="pb-8">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Zap className="w-6 h-6 text-primary" />
                  Free
                </CardTitle>
                <Badge variant="outline">Perfect to Start</Badge>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-5xl font-bold">₹0</span>
                <span className="text-muted-foreground">/month</span>
              </div>
              <CardDescription className="text-base">
                Great for trying out PromptCraft AI and exploring AI-powered prompt generation
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {features.free.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    )}
                    <span className={feature.included ? 'text-foreground' : 'text-muted-foreground'}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={onBackToHome}
                className="w-full" 
                variant="outline"
                size="lg"
              >
                Get Started Free
              </Button>
            </CardFooter>
          </Card>

          {/* Pro Plan */}
          <Card className="relative overflow-hidden border-2 border-primary shadow-2xl hover:shadow-primary/20 transition-all duration-300 animate-slide-up" style={{ animationDelay: '0.1s' }}>
            {/* Popular Badge */}
            <div className="absolute top-0 right-0 bg-gradient-to-r from-primary to-secondary text-primary-foreground px-6 py-2 rounded-bl-lg font-semibold text-sm flex items-center gap-1">
              <Crown className="w-4 h-4" />
              Most Popular
            </div>
            
            <CardHeader className="pb-8 pt-12">
              <div className="flex items-center justify-between mb-4">
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Crown className="w-6 h-6 text-primary" />
                  Pro
                </CardTitle>
                <Badge className="bg-gradient-to-r from-primary to-secondary">Best Value</Badge>
              </div>
              <div className="space-y-2 mb-2">
                {!isAnnual ? (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold">₹{monthlyPrice}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold">₹{annualPrice}</span>
                      <span className="text-muted-foreground">/year</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Billed annually at ₹{annualPrice}
                    </div>
                  </>
                )}
              </div>
              <CardDescription className="text-base">
                For professionals who need unlimited access to advanced AI prompt engineering
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 mb-6">
                {features.pro.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    {feature.included ? (
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    ) : (
                      <X className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                    )}
                    <span className={feature.included ? 'text-foreground font-medium' : 'text-muted-foreground'}>
                      {feature.name}
                    </span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button 
                onClick={handleUpgradeToPro}
                className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90" 
                size="lg"
                disabled={currentUser && currentUser.is_pro}
              >
                <Rocket className="w-5 h-5 mr-2" />
                {currentUser && currentUser.is_pro ? 'Current Plan ✓' : 'Upgrade to Pro'}
              </Button>
            </CardFooter>
          </Card>
        </div>

        {/* Feature Comparison Table */}
        <div className="mb-20 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <h2 className="text-3xl font-bold text-center mb-8">Detailed Feature Comparison</h2>
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-4 font-semibold">Feature</th>
                      <th className="text-center p-4 font-semibold">Free</th>
                      <th className="text-center p-4 font-semibold">Pro</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    <tr>
                      <td className="p-4 font-medium">Monthly Prompt Generations</td>
                      <td className="text-center p-4">10</td>
                      <td className="text-center p-4 font-bold text-primary">Unlimited</td>
                    </tr>
                    <tr className="bg-muted/20">
                      <td className="p-4 font-medium">AI Processing Speed</td>
                      <td className="text-center p-4">Standard</td>
                      <td className="text-center p-4 font-bold text-primary">2x Faster</td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Prompt Length</td>
                      <td className="text-center p-4">Up to 5,000 chars</td>
                      <td className="text-center p-4 font-bold text-primary">Up to 15,000 chars</td>
                    </tr>
                    <tr className="bg-muted/20">
                      <td className="p-4 font-medium">Advanced Prompting Techniques</td>
                      <td className="text-center p-4"><X className="w-5 h-5 text-muted-foreground mx-auto" /></td>
                      <td className="text-center p-4"><Check className="w-5 h-5 text-primary mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">Custom Templates</td>
                      <td className="text-center p-4"><X className="w-5 h-5 text-muted-foreground mx-auto" /></td>
                      <td className="text-center p-4"><Check className="w-5 h-5 text-primary mx-auto" /></td>
                    </tr>
                    <tr className="bg-muted/20">
                      <td className="p-4 font-medium">Priority Support</td>
                      <td className="text-center p-4"><X className="w-5 h-5 text-muted-foreground mx-auto" /></td>
                      <td className="text-center p-4"><Check className="w-5 h-5 text-primary mx-auto" /></td>
                    </tr>
                    <tr>
                      <td className="p-4 font-medium">API Access</td>
                      <td className="text-center p-4"><X className="w-5 h-5 text-muted-foreground mx-auto" /></td>
                      <td className="text-center p-4 font-bold text-primary">Coming Soon</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Why Choose Pro Section */}
        <div className="mb-20 animate-slide-up" style={{ animationDelay: '0.3s' }}>
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Pro?</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                  <Rocket className="w-6 h-6 text-primary" />
                </div>
                <CardTitle className="text-xl">Unlimited Creativity</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Generate as many AI-optimized prompts as you need. No limits, no restrictions. Perfect for content creators and businesses.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-secondary" />
                </div>
                <CardTitle className="text-xl">2x Faster Processing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Get your prompts generated in half the time with priority AI processing. More productivity, less waiting.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-primary transition-colors">
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mb-4">
                  <HeadphonesIcon className="w-6 h-6 text-accent" />
                </div>
                <CardTitle className="text-xl">Premium Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Get 24/7 priority support via email and chat. Our team is always ready to help you maximize your results.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-20 animate-slide-up" style={{ animationDelay: '0.4s' }}>
          <h2 className="text-3xl font-bold text-center mb-12">Frequently Asked Questions</h2>
          <div className="max-w-3xl mx-auto space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I upgrade or downgrade my plan anytime?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes! You can upgrade to Pro at any time. If you&apos;re on Pro and want to downgrade, you can do so at the end of your billing cycle.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What payment methods do you accept?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We accept all major credit/debit cards (Visa, Mastercard, American Express), UPI, net banking, and digital wallets. All payments are processed securely.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Is there a refund policy?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Yes, we offer a 7-day money-back guarantee on Pro plans. If you&apos;re not satisfied, contact our support team for a full refund within 7 days of purchase.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Do unused prompts roll over to the next month?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  For Free plan users, unused prompts do not roll over. Pro plan users have unlimited prompts, so there&apos;s no need to worry about limits.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I use PromptCraft AI for commercial projects?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Absolutely! Both Free and Pro plans allow commercial use of generated prompts. Pro plan is recommended for businesses and agencies with higher volume needs.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center animate-slide-up" style={{ animationDelay: '0.5s' }}>
          <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5">
            <CardContent className="py-12">
              <div className="flex items-center justify-center mb-4">
                <img 
                  src="/growthyfai-logo.png" 
                  alt="Growthyfai" 
                  className="h-16 w-auto"
                />
              </div>
              <h2 className="text-3xl font-bold mb-4">Ready to Transform Your Prompts?</h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                Join thousands of professionals using PromptCraft AI to create powerful, detailed prompts in seconds
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={onBackToHome}
                  size="lg" 
                  className="bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Free
                </Button>
                <Button 
                  onClick={onBackToHome}
                  size="lg" 
                  variant="outline"
                >
                  View Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Trust Badges */}
        <div className="mt-16 text-center animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <p className="text-sm text-muted-foreground mb-6">Trusted by professionals worldwide</p>
          <div className="flex flex-wrap justify-center items-center gap-8 text-muted-foreground">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              <span className="text-sm">Secure Payments</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span className="text-sm">10,000+ Users</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-5 h-5 fill-current text-gold" />
              <span className="text-sm">4.9/5 Rating</span>
            </div>
            <div className="flex items-center gap-2">
              <HeadphonesIcon className="w-5 h-5" />
              <span className="text-sm">24/7 Support</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
